import { CSSProperties, FC, ReactElement } from 'react';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Image, Row } from 'react-bootstrap';
import {
  ISubscription,
  ITrainingConsultation,
  ITrainingVideo
} from '../../domain/interfaces/api/ITraining';
import { ServiceTypeEnum } from '../../domain/enums/ServiceTypeEnum';
import helpers from '../../util/helpers';
import PccVideo from '../PccVideo/PccVideo';
import { getMinPriceByType } from '../../domain/utils';
import './Training.css';

const checkIsNewTraining = (element: any): boolean => {
  // Tryb testowy: true = wymusza wyświetlenie do oceny wyglądu
  const FORCE_TEST_BADGE = false;
  if (FORCE_TEST_BADGE) return true;

  if (!element) return false;

  const rawDate =
    element.created_at ??
    element.createdAt ??
    element.date_start ??
    element.date ??
    element.creation_date;

  if (!rawDate) return false;

  let timestampMs: number;
  if (typeof rawDate === 'number' || (!isNaN(Number(rawDate)) && !isNaN(parseFloat(rawDate)))) {
    const num = Number(rawDate);
    timestampMs = num < 10000000000 ? num * 1000 : num;
  } else {
    timestampMs = new Date(rawDate).getTime();
  }

  if (isNaN(timestampMs)) return false;

  const now = new Date().getTime();
  const diffInDays = (now - timestampMs) / (1000 * 60 * 60 * 24);

  return diffInDays >= 0 && diffInDays <= 14;
};

const renderTrainingMedia = (
  subscriptionType: ServiceTypeEnum,
  element: ITrainingConsultation | ITrainingVideo
) => {
  const isVideo = subscriptionType === ServiceTypeEnum.VIDEO && element.trailer_url;
  const isConsultation = subscriptionType === ServiceTypeEnum.CONSULTANCY && element.consultation_trailer_url;

  if (isVideo || isConsultation) {
    return (
      <PccVideo
        videoPath={String(helpers.getVideoUrl(subscriptionType, element.trailer_url, element.trailer_url))}
      />
    );
  }

  return (
    <Image
      loading="lazy"
      className="w-100 h-100"
      style={{ objectFit: 'cover', borderRadius: '4px' }}
      height={200}
      src={helpers.formatTrainingImage(element.image_url)}
    />
  );
};

const TrainingItem: FC<{
  element: ITrainingConsultation | ITrainingVideo;
  type: ServiceTypeEnum;
  styles?: CSSProperties;
}> = ({ element, type, styles }): ReactElement => {
  let shortDescription = helpers.getTrainingShorDescription(
    type,
    element.short_description,
    element.short_description_video
  );

  const description = `${shortDescription.substring(0, 150)}${
    shortDescription.length > 150 ? ' (...)' : ''
  }`;

  const renderPrice = (subscriptions: ISubscription[]): string => {
    const result = getMinPriceByType(subscriptions, type);
    return result ? `PLN ${result.net_price}` : '';
  };

  const showBadge = checkIsNewTraining(element);

  return (
    <Col
      className="training-item d-flex p-4 flex-column h-100 position-relative"
      style={styles}
    >
      {showBadge && (
        <div className="training-badge-new">
          <span className="training-badge-dot" />
          NOWOŚĆ
        </div>
      )}

      <Row style={{ height: 200, margin: 0, padding: 0 }}>
        {renderTrainingMedia(type, element)}
      </Row>

      <Row className="mt-4 flex-grow-1 flex-column">
        <a
          className="text-dark text-decoration-none"
          href={`/${type}/${element.slug}`}
        >
          <h3 className="training-item-title">{element.name}</h3>
          <div
            className="training-item-desc"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </a>
      </Row>

      <div
        className={`d-flex align-items-center training-details pb-3 mt-2${
          element.instructor ? ' border-bottom' : ''
        }`}
      >
        {element.level && (
          <Col>
            <FontAwesomeIcon icon={faChartLine} className="me-2" />
            <span>{element.level.name}</span>
          </Col>
        )}
      </div>

      {element.instructor && (
        <Row className="flex-row align-items-center mt-3">
          <Col className="d-flex align-self-start align-items-center">
            <Image
              loading="lazy"
              src={element.instructor.photo_url || '/img/avatar.jpg'}
              width="40"
              height="40"
              style={{ objectFit: 'cover', borderRadius: '50%' }}
            />
            <span className="ms-2" style={{ fontWeight: 500 }}>
              {element.instructor.name}
            </span>
          </Col>

          <span
            className={`training-price${type === ServiceTypeEnum.VIDEO ? ' video' : ''}`}
            style={{ fontWeight: 700, fontSize: '1.05rem', textAlign: 'right' }}
          >
            {renderPrice(element.subscriptions)}
          </span>
        </Row>
      )}
    </Col>
  );
};

export default TrainingItem;